import { createTableAIProfile, copyTableAIProfile, type ITableAIProfile } from './table-ai';
import {
  openingStackIndex,
  seatWindsFromOpening,
  selectInitialDealer,
  selectOpeningSeat,
  wallStartStackIndex,
  type TDiceValues,
} from './dice-rules';
import { RoundOutcome, transitionAfterRound } from './game-set-transition';
import { RoundIndex, Seat, Wind, type TRoundIndex, type TSeat, type TWind } from './types';

const GAME_SET_SCHEMA_VERSION = 1;

export const GameSetStatus = {
  Setup: 'setup',
  Ready: 'ready',
  RoundInProgress: 'round-in-progress',
  BetweenRounds: 'between-rounds',
  CircleComplete: 'circle-complete',
  Completed: 'completed',
} as const;

export type TGameSetStatus = (typeof GameSetStatus)[keyof typeof GameSetStatus];

export const GameSetCompletionReason = {
  FinishedNorthCircle: 'finished-north-circle',
  InsufficientBalance: 'insufficient-balance',
} as const;

export type TGameSetCompletionReason =
  (typeof GameSetCompletionReason)[keyof typeof GameSetCompletionReason];

export const BetPlans = [
  { base: 50, taiUnit: 10 },
  { base: 100, taiUnit: 20 },
  { base: 200, taiUnit: 50 },
  { base: 500, taiUnit: 100 },
  { base: 1000, taiUnit: 200 },
] as const;

export interface IBetPlan {
  readonly base: number;
  readonly taiUnit: number;
}

export interface IRoundPlan {
  readonly roundId: string;
  readonly transactionId: string;
  readonly dealerSeat: TSeat;
  readonly dealerStreak: number;
  readonly prevailingWind: TWind;
  readonly roundIndex: TRoundIndex;
  readonly openingSeat: TSeat;
  readonly openingStackIndex: number;
  readonly wallStartStackIndex: number;
  readonly seatWinds: readonly [TWind, TWind, TWind, TWind];
  readonly dealerDice: TDiceValues | null;
  readonly openingDice: TDiceValues;
  readonly wallSeed: number;
  readonly base: number;
  readonly taiUnit: number;
  readonly settlementApplied: boolean;
}

export interface IGameSetPlayer {
  readonly id: string;
  readonly name: string;
  readonly seat: TSeat;
}

export interface IGameSetSession {
  readonly tableAI?: ITableAIProfile;
  readonly schemaVersion: number;
  readonly sessionId: string;
  readonly status: TGameSetStatus;
  readonly completionReason: TGameSetCompletionReason | null;
  readonly players: readonly [IGameSetPlayer, IGameSetPlayer, IGameSetPlayer, IGameSetPlayer];
  readonly playerSeat: TSeat;
  readonly fakeEastSeat: TSeat;
  readonly initialDealerSeat: TSeat;
  readonly dealerSeat: TSeat;
  readonly prevailingWind: TWind;
  readonly roundIndex: TRoundIndex;
  readonly dealerStreak: number;
  readonly completedRounds: number;
  readonly completedCircles: number;
  readonly startingBalances: readonly [number, number, number, number];
  readonly bet: IBetPlan;
  readonly currentRound: IRoundPlan | null;
}

export interface ICreateGameSetInput {
  readonly tableAI?: ITableAIProfile;
  readonly sessionId: string;
  readonly roundId: string;
  readonly transactionId: string;
  readonly balances: readonly [number, number, number, number];
  readonly bet: IBetPlan;
  readonly dealerDice: TDiceValues;
  readonly openingDice: TDiceValues;
  readonly wallSeed: number;
  readonly playerSeat?: TSeat;
  readonly fakeEastSeat?: TSeat;
}

export interface IPrepareNextRoundInput {
  readonly roundId: string;
  readonly transactionId: string;
  readonly openingDice: TDiceValues;
  readonly wallSeed: number;
  readonly bet?: IBetPlan;
}

export const minimumBalanceFor = (bet: IBetPlan): number => bet.base + bet.taiUnit;

export const canAllPlayersAfford = (
  balances: readonly [number, number, number, number],
  bet: IBetPlan
): boolean => balances.every((balance) => balance >= minimumBalanceFor(bet));

export const affordableBetPlans = (
  balances: readonly [number, number, number, number]
): readonly IBetPlan[] => BetPlans.filter((bet) => canAllPlayersAfford(balances, bet));

export const createGameSet = (input: ICreateGameSetInput): IGameSetSession => {
  if (!canAllPlayersAfford(input.balances, input.bet)) {
    throw new Error('All four players must afford the selected bet');
  }
  const playerSeat = input.playerSeat ?? Seat.Self;
  const fakeEastSeat = input.fakeEastSeat ?? Seat.Self;
  const dealerSeat = selectInitialDealer(fakeEastSeat, input.dealerDice);
  const openingSeat = selectOpeningSeat(dealerSeat, input.openingDice);
  const seatWinds = seatWindsFromOpening(openingSeat);

  return {
    schemaVersion: GAME_SET_SCHEMA_VERSION,
    tableAI: input.tableAI ? copyTableAIProfile(input.tableAI) : createTableAIProfile(),
    sessionId: input.sessionId,
    status: GameSetStatus.RoundInProgress,
    completionReason: null,
    players: [
      { id: 'player-self', name: '市民', seat: Seat.Self },
      { id: 'npc-right', name: '史豔文', seat: Seat.Right },
      { id: 'npc-opposite', name: '水手金星', seat: Seat.Opposite },
      { id: 'npc-left', name: '無敵鐵金剛', seat: Seat.Left },
    ],
    playerSeat,
    fakeEastSeat,
    initialDealerSeat: dealerSeat,
    dealerSeat,
    prevailingWind: Wind.East,
    roundIndex: RoundIndex.East,
    dealerStreak: 0,
    completedRounds: 0,
    completedCircles: 0,
    startingBalances: [...input.balances],
    bet: { ...input.bet },
    currentRound: {
      roundId: input.roundId,
      transactionId: input.transactionId,
      dealerSeat,
      dealerStreak: 0,
      prevailingWind: Wind.East,
      roundIndex: RoundIndex.East,
      openingSeat,
      openingStackIndex: openingStackIndex(input.openingDice),
      wallStartStackIndex: wallStartStackIndex(openingSeat, input.openingDice),
      seatWinds,
      dealerDice: input.dealerDice,
      openingDice: input.openingDice,
      wallSeed: input.wallSeed,
      base: input.bet.base,
      taiUnit: input.bet.taiUnit,
      settlementApplied: false,
    },
  };
};

export const advanceGameSetAfterRound = (
  session: IGameSetSession,
  winnerSeat: TSeat | null
): IGameSetSession => {
  const outcome =
    winnerSeat === null
      ? RoundOutcome.Draw
      : winnerSeat === session.dealerSeat
        ? RoundOutcome.DealerWin
        : RoundOutcome.NonDealerWin;
  const transition = transitionAfterRound(
    {
      initialDealerSeat: session.initialDealerSeat,
      dealerSeat: session.dealerSeat,
      dealerStreak: session.dealerStreak,
      prevailingWind: session.prevailingWind,
      roundIndex: session.roundIndex,
      completedRounds: session.completedRounds,
      completedCircles: session.completedCircles,
    },
    outcome
  );
  const status = transition.sessionCompleted
    ? GameSetStatus.Completed
    : transition.circleCompleted
      ? GameSetStatus.CircleComplete
      : GameSetStatus.BetweenRounds;

  return {
    ...session,
    status,
    completionReason: transition.sessionCompleted
      ? GameSetCompletionReason.FinishedNorthCircle
      : null,
    dealerSeat: transition.dealerSeat,
    dealerStreak: transition.dealerStreak,
    prevailingWind: transition.prevailingWind,
    roundIndex: transition.roundIndex,
    completedRounds: transition.completedRounds,
    completedCircles: transition.completedCircles,
    currentRound: null,
  };
};

export const completeGameSetForInsufficientBalance = (
  session: IGameSetSession
): IGameSetSession => ({
  ...session,
  status: GameSetStatus.Completed,
  completionReason: GameSetCompletionReason.InsufficientBalance,
  currentRound: null,
});

export const prepareNextRound = (
  session: IGameSetSession,
  input: IPrepareNextRoundInput
): IGameSetSession => {
  if (
    session.status !== GameSetStatus.BetweenRounds &&
    session.status !== GameSetStatus.CircleComplete
  ) {
    throw new Error(`Cannot prepare next round from status ${session.status}`);
  }
  const bet = input.bet ?? session.bet;
  const openingSeat = selectOpeningSeat(session.dealerSeat, input.openingDice);
  return {
    ...session,
    status: GameSetStatus.RoundInProgress,
    bet: { ...bet },
    currentRound: {
      roundId: input.roundId,
      transactionId: input.transactionId,
      dealerSeat: session.dealerSeat,
      dealerStreak: session.dealerStreak,
      prevailingWind: session.prevailingWind,
      roundIndex: session.roundIndex,
      openingSeat,
      openingStackIndex: openingStackIndex(input.openingDice),
      wallStartStackIndex: wallStartStackIndex(openingSeat, input.openingDice),
      seatWinds: seatWindsFromOpening(openingSeat),
      dealerDice: null,
      openingDice: input.openingDice,
      wallSeed: input.wallSeed,
      base: bet.base,
      taiUnit: bet.taiUnit,
      settlementApplied: false,
    },
  };
};

export { RoundIndex };
export type { TRoundIndex };
